import {
  AssignmentIndOutlined,
  BadgeOutlined,
  CakeOutlined,
  CheckCircleOutline,
  ContactEmergencyOutlined,
  EmailOutlined,
  FavoriteBorderOutlined,
  HomeOutlined,
  PersonOutlineOutlined,
  PhoneOutlined,
  SchoolOutlined,
  TransgenderOutlined,
  VerifiedUserOutlined,
  VisibilityOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ClientDropdown, { ClientEditButton } from "components/Dropdowns/ClientDropdown";
import ClientDetailsModal from "components/Clients/ClientDetailsModal";
import { formatDisplayDate } from "utils/dateFormat";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { createAdmission, patchApplicantDetails } from "./admissionApi";
import {
  DEFAULT_APPLICATION_STATUS,
  getClientProfileCompleteness,
} from "./clientProfileCompleteness";

const getInitials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

const Field = ({ icon, label, value, required = false, showCompleteness = false }) => {
  const display = value || "—";
  const isMissing = required && !value;

  return (
    <div className="flex items-start gap-2.5 py-1">
      <span className="mt-0.5 text-app-text-muted shrink-0">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-xs uppercase tracking-wide text-app-text-muted">{label}</p>
        <p className="text-sm font-medium break-words text-app-text">{display}</p>
      </div>
      {showCompleteness &&
        (isMissing ? (
          <WarningAmberOutlined
            className="text-amber-600 dark:text-amber-400 shrink-0"
            fontSize="small"
          />
        ) : (
          <CheckCircleOutline
            className="text-emerald-600 dark:text-emerald-400 shrink-0"
            fontSize="small"
          />
        ))}
    </div>
  );
};

const SectionCard = ({ title, children, className = "" }) => (
  <Paper variant="outlined" className={`p-4 border-app-border ${className}`}>
    <Typography variant="overline" className="text-orange-600 font-semibold tracking-wide">
      {title}
    </Typography>
    <div className="mt-3 grid gap-2 sm:grid-cols-2">{children}</div>
  </Paper>
);

const GeneralInfo = ({
  generalInfo,
  setGeneralInfo,
  clientLocked = false,
  admissionId,
  onAdmissionCreated,
  onSaved,
}) => {
  const hasClient = Boolean(generalInfo?.clientId);
  const [isSaving, setIsSaving] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  const handleSave = async () => {
    if (!generalInfo?.clientId) {
      toast.error("Select a client before saving.");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: generalInfo?.name,
        email: generalInfo?.email,
        number: generalInfo?.number,
        dob: generalInfo?.dob,
        gender: generalInfo?.gender,
        address: generalInfo?.address,
        passportNumber: generalInfo?.passportNumber,
        passportExpiry: generalInfo?.passportExpiry,
        dateOfIssue: generalInfo?.dateOfIssue,
        visaStatus: generalInfo?.visaStatus,
        visaExpiry: generalInfo?.visaExpiry,
        passportFile: generalInfo?.passportFile,
        passport: generalInfo?.passport,
        emergency: {
          name: generalInfo?.emergencyContactName ?? generalInfo?.emergency?.name,
          email: generalInfo?.emergencyEmail ?? generalInfo?.emergency?.email,
          contact: generalInfo?.emergencyContact ?? generalInfo?.emergency?.contact,
          relation: generalInfo?.emergencyRelation ?? generalInfo?.emergency?.relation,
        },
        maritalStatus: generalInfo?.maritalStatus,
        permanent: generalInfo?.permanent,
        temp: generalInfo?.temp,
        criminalOffence: generalInfo?.criminalOffence,
        causeOfCriminalOffence: generalInfo?.causeOfCriminalOffence,
        immigrationToOtherCountry: generalInfo?.immigrationToOtherCountry,
        immigratedCountry: generalInfo?.immigratedCountry,
        moreThanOneCitizen: generalInfo?.moreThanOneCitizen,
        refusedFromAnyCountry: generalInfo?.refusedFromAnyCountry,
        secondCountryName: generalInfo?.secondCountryName,
        livingInAnotherCountry: generalInfo?.livingInAnotherCountry,
        secondLivingCountryName: generalInfo?.secondLivingCountryName,
        refusedCountryName: generalInfo?.refusedCountryName,
        seriousMedicalCondition: generalInfo?.seriousMedicalCondition,
        medicalCause: generalInfo?.medicalCause,
        academicInfo: generalInfo?.academicInfo,
        testInfo: generalInfo?.testInfo,
        onShoreStatus: generalInfo?.onShoreStatus,
        studentId: generalInfo?.studentId,
      };

      if (!admissionId) {
        const created = await createAdmission({
          clientId: generalInfo.clientId,
          status: generalInfo?.status ?? "Incomplete Application",
          onShoreStatus: generalInfo?.onShoreStatus ?? true,
          active: true,
          completeStatus: false,
          ...payload,
        });
        const newId = created?.id ?? created?.data?.id;
        if (newId) {
          onAdmissionCreated?.(newId);
          toast.success("Admission created.");
        }
      } else {
        await patchApplicantDetails(admissionId, payload);
        toast.success("Applicant details saved.");
        onSaved?.();
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ?? err?.response?.data?.errorMessage ?? "Failed to save."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const { isComplete, missing } = useMemo(
    () => getClientProfileCompleteness(hasClient ? generalInfo : null),
    [generalInfo, hasClient]
  );

  console.log(isComplete, missing);

  const showFieldStatus = hasClient && !isComplete;

  const mergeGeneralInfo = (updater) => {
    setGeneralInfo((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return {
        ...next,
        // preserve admission id — client data (from ClientEditButton) must not overwrite it
        id: prev?.id,
        status: next?.status || prev?.status || DEFAULT_APPLICATION_STATUS,
      };
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        {/* Client picker only when the client can still be changed */}

        <ClientDropdown
          data={generalInfo}
          setData={mergeGeneralInfo}
          hideEditButton
          outerClassName="w-full sm:w-1/3 min-w-[220px]"
          disabled={clientLocked}
        />

        <div className="flex flex-wrap items-center gap-2">
          <FormControlLabel
            control={
              <Switch
                size="small"
                onChange={(e) => {
                  setGeneralInfo((p) => ({ ...p, onShoreStatus: e.target.checked }));
                }}
                checked={Boolean(generalInfo?.onShoreStatus)}
              />
            }
            label={generalInfo?.onShoreStatus ? "Onshore" : "Offshore"}
          />
          <TextField
            label="Application status"
            value={generalInfo?.status || DEFAULT_APPLICATION_STATUS}
            size="small"
            InputProps={{ readOnly: true }}
            helperText="Updates from the Application form tab. Withdraw there too."
            sx={{ minWidth: { xs: "100%", sm: 260 } }}
          />
        </div>
      </div>

      {hasClient && (
        <>
          {!isComplete && (
            <Alert
              severity="warning"
              icon={<WarningAmberOutlined fontSize="inherit" />}
              className="border border-amber-200"
            >
              <Typography variant="subtitle2" className="font-semibold">
                Incomplete information
              </Typography>
              <Typography variant="body2" className="mb-2">
                Some client details are missing. You can still save this admission — update the
                client record to fill gaps before lodging.
              </Typography>
              <div className="flex flex-wrap gap-1.5">
                {missing.map((field) => (
                  <Chip
                    key={field.key}
                    size="small"
                    label={field.label}
                    variant="outlined"
                    color="warning"
                  />
                ))}
              </div>
            </Alert>
          )}

          <Paper
            variant="outlined"
            className="p-5 border-app-border bg-gradient-to-r from-app-surface-alt to-app-surface-muted"
          >
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <Avatar sx={{ width: 56, height: 56, bgcolor: "#ea580c", fontSize: 22 }}>
                  {getInitials(generalInfo?.name)}
                </Avatar>
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wide text-app-text-muted">
                    Client profile
                  </p>
                  <h3 className="text-lg font-semibold text-app-text truncate m-0">
                    {generalInfo?.name || "Unnamed client"}
                  </h3>
                  <p className="text-sm text-app-text-muted truncate m-0 mt-0.5">
                    {[generalInfo?.email, generalInfo?.number].filter(Boolean).join(" · ") ||
                      "No contact on file"}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {isComplete ? (
                  <Tooltip title="This client profile has the information needed for admission processing.">
                    <Chip
                      size="small"
                      icon={<CheckCircleOutline />}
                      label="Profile complete"
                      color="success"
                      variant="outlined"
                    />
                  </Tooltip>
                ) : (
                  <Chip
                    size="small"
                    icon={<WarningAmberOutlined />}
                    label={`${missing.length} field${missing.length === 1 ? "" : "s"} missing`}
                    color="warning"
                    variant="outlined"
                  />
                )}
                <ClientEditButton data={generalInfo} setData={mergeGeneralInfo} variant="button" />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<VisibilityOutlined />}
                  onClick={() => setShowProfile(true)}
                >
                  View profile
                </Button>
              </div>
            </div>
          </Paper>

          <div>
            <Typography variant="subtitle1" className="font-semibold text-app-text">
              Client details
            </Typography>
            <Typography variant="body2" color="text.secondary" className="mt-0.5 mb-3">
              Read-only summary from the client record. Use Edit client above to update fields.
            </Typography>

            <div className="grid gap-4 md:grid-cols-2">
              <SectionCard title="Personal information">
                <Field
                  icon={<PersonOutlineOutlined fontSize="small" />}
                  label="Full name"
                  value={generalInfo?.name}
                  required
                  showCompleteness={showFieldStatus}
                />
                <Field
                  icon={<CakeOutlined fontSize="small" />}
                  label="Date of birth"
                  value={formatDisplayDate(generalInfo?.dob)}
                  required
                  showCompleteness={showFieldStatus}
                />
                <Field
                  icon={<TransgenderOutlined fontSize="small" />}
                  label="Gender"
                  value={generalInfo?.gender}
                  required
                  showCompleteness={showFieldStatus}
                />
                <Field
                  icon={<FavoriteBorderOutlined fontSize="small" />}
                  label="Marital status"
                  value={generalInfo?.maritalStatus}
                  required
                  showCompleteness={showFieldStatus}
                />
              </SectionCard>

              <SectionCard title="Contact details">
                <Field
                  icon={<EmailOutlined fontSize="small" />}
                  label="Email"
                  value={generalInfo?.email}
                  required
                  showCompleteness={showFieldStatus}
                />
                <Field
                  icon={<PhoneOutlined fontSize="small" />}
                  label="Phone"
                  value={generalInfo?.number}
                  required
                  showCompleteness={showFieldStatus}
                />
                <Field
                  icon={<HomeOutlined fontSize="small" />}
                  label="Address"
                  value={generalInfo?.address}
                  required
                  showCompleteness={showFieldStatus}
                />
                <Field
                  icon={<SchoolOutlined fontSize="small" />}
                  label="Case officer"
                  value={generalInfo?.caseOfficer}
                />
              </SectionCard>

              <SectionCard title="Identity & visa">
                <Field
                  icon={<BadgeOutlined fontSize="small" />}
                  label="Passport number"
                  value={generalInfo?.passportNumber}
                  required
                  showCompleteness={showFieldStatus}
                />
                <Field
                  icon={<AssignmentIndOutlined fontSize="small" />}
                  label="USI number"
                  value={generalInfo?.usiNumber}
                />
                <Field
                  icon={<VerifiedUserOutlined fontSize="small" />}
                  label="Passport expiry"
                  value={formatDisplayDate(generalInfo?.passportExpiry)}
                  required
                  showCompleteness={showFieldStatus}
                />
                <Field
                  icon={<VerifiedUserOutlined fontSize="small" />}
                  label="Visa status"
                  value={generalInfo?.visaStatus}
                />
                <Field
                  icon={<VerifiedUserOutlined fontSize="small" />}
                  label="Visa expiry"
                  value={formatDisplayDate(generalInfo?.visaExpiry)}
                />
              </SectionCard>

              <SectionCard title="Emergency contact">
                <Field
                  icon={<ContactEmergencyOutlined fontSize="small" />}
                  label="Name"
                  value={generalInfo?.emergencyContactName}
                  required
                  showCompleteness={showFieldStatus}
                />
                <Field
                  icon={<PersonOutlineOutlined fontSize="small" />}
                  label="Relation"
                  value={generalInfo?.emergencyRelation}
                  required
                  showCompleteness={showFieldStatus}
                />
                <Field
                  icon={<PhoneOutlined fontSize="small" />}
                  label="Phone"
                  value={generalInfo?.emergencyContact}
                  required
                  showCompleteness={showFieldStatus}
                />
                <Field
                  icon={<EmailOutlined fontSize="small" />}
                  label="Email"
                  value={generalInfo?.emergencyEmail}
                  required
                  showCompleteness={showFieldStatus}
                />
              </SectionCard>
            </div>
          </div>
        </>
      )}

      <div className="mt-6 flex justify-end border-t border-app-border pt-4">
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} sx={{ color: "white" }} /> : null}
        >
          {isSaving ? "Saving…" : admissionId ? "Save applicant details" : "Create admission"}
        </Button>
      </div>

      {showProfile && (
        <ClientDetailsModal
          open={showProfile}
          handleClose={() => setShowProfile(false)}
          clientId={generalInfo?.clientId}
        />
      )}
    </div>
  );
};

export default GeneralInfo;
